import { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import useEnhancedFormik from '../../hooks/useEnhancedFormik'
import { STEPS } from '../../common/constants'
import { shippingSend3ShipmentPropTypes } from '../../common/propTypes'
import { nullablePropType } from '../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../common/formikPropTypes'
import { generateTouchedObject } from '../../libs/formik'
import { generateRandomKey } from '../../utils'
import ConfirmationWithErrorModal from '../../ui/modals/confirmationWithError'
import ActionButtons from '../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../ui/views/shippingSend/FormWrapper'
import Step3ShipmentUI from '../../ui/views/shippingSend/Step3Shipment'

import useShippingSendStep from './useShippingSendStep'
import { useServiceTypes } from '../../services/user'
import useTranslations from '../../i18n/useTranslations'
import useDebounce from './useDebounce'

const SINGLE_PACKAGE_MAXIMUM_WEIGHT = 20
const SINGLE_PACKAGE_MAXIMUM_MEASURE = 60
const ALL_PACKAGE_MEASURES_MAXIMUM_SUM = 160

const checkPackageHasContent = packageContent =>
  (packageContent.weight !== null) |
  (packageContent.length !== null) |
  (packageContent.width !== null) |
  (packageContent.height !== null)

export const computeShippingWeight = (weight, dimensions) => {
  if (dimensions.length < 0 || dimensions.width < 0 || dimensions.height < 0)
    throw new Error('length, width and height must be greater or equal to zero')

  if (dimensions.length == 0 || dimensions.width == 0 || dimensions.height == 0)
    return weight

  const volumentricWeight =
    (dimensions.length * dimensions.width * dimensions.height * 167) / 1000000

  return weight > volumentricWeight ? weight : volumentricWeight
}

const ShipmentSchema = requiredMeasures =>
  Yup.object().shape({
    serviceType: Yup.object().nullable().required(),

    customsSelected: Yup.object(),

    referenceNumber: Yup.string(),

    hasCustom: Yup.object().nullable(),

    packages: Yup.array()
      .of(
        Yup.object()
          .shape({
            weight: Yup.number()
              .nullable()
              .when('isPudo', (isPudo, schema) =>
                isPudo
                  ? schema
                      .min(0.1)
                      .max(SINGLE_PACKAGE_MAXIMUM_WEIGHT)
                      .required()
                  : schema.min(0.1).required(),
              ),
            length: Yup.number()
              .nullable()
              .when(['isPudo', 'serviceType'], (isPudo, serviceType, schema) =>
                requiredMeasures
                  ? isPudo || serviceType?.originalCode === 'CIEL'
                    ? schema
                        .min(0)
                        .max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                        .required()
                    : schema.min(0).required()
                  : isPudo
                  ? schema.min(0).max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                  : schema.min(0),
              ),
            width: Yup.number()
              .nullable()
              .when(['isPudo', 'serviceType'], (isPudo, serviceType, schema) =>
                requiredMeasures
                  ? isPudo || serviceType?.originalCode === 'CIEL'
                    ? schema
                        .min(0)
                        .max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                        .required()
                    : schema.min(0).required()
                  : isPudo
                  ? schema.min(0).max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                  : schema.min(0),
              ),
            height: Yup.number()
              .nullable()
              .when(['isPudo', 'serviceType'], (isPudo, serviceType, schema) =>
                requiredMeasures
                  ? isPudo || serviceType?.originalCode === 'CIEL'
                    ? schema
                        .min(0)
                        .max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                        .required()
                    : schema.min(0).required()
                  : isPudo
                  ? schema.min(0).max(SINGLE_PACKAGE_MAXIMUM_MEASURE)
                  : schema.min(0),
              ),
          })
          .required(),
      )
      .min(1)
      .required(),
    isPudo: Yup.bool(),
  })

const INITIAL_VALUES = {
  serviceType: null,
  hasCustom: null,
  customsSelected: { label: 'Destinatario', value: 'RECIPIENT' },
  referenceNumber: '',
  packages: [
    {
      key: generateRandomKey(),
      weight: null,
      length: null,
      width: null,
      height: null,
    },
  ],
}

const Step3Shipment = ({
  centreCode,
  originPostalCode,
  originCountryCode,
  destinPostalCode,
  destinCountryCode,
  originOrDestinAddressChanged,
  originAddressChanged,
  savedValues,
  activeStep,
  setCustom,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
  onSetDirty,
  hasConfirmation,
  onCancelConfirmation,
  onAcceptConfirmation,
  isPudo,
  isOriginPudo,
  international,
  imputationCenter,
}) => {
  const requiredMeasures = () => {
    return (
      (destinCountryCode === 'ES' && destinPostalCode.substr(0, 2) === '38') ||
      destinPostalCode.substr(0, 2) === '35' ||
      destinPostalCode.substr(0, 2) === '51' ||
      destinPostalCode.substr(0, 2) === '52' ||
      (originCountryCode === 'ES' &&
        (originPostalCode.substr(0, 2) === '38' ||
          originPostalCode.substr(0, 2) === '35' ||
          originPostalCode.substr(0, 2) === '51' ||
          originPostalCode.substr(0, 2) === '52'))
    )
  }

  const t = useTranslations()
  const [merchandise, setMerchandise] = useState(true)
  const [completedPackages, setCompletedPackages] = useState([])

  const formik = useFormik({
    validateOnChange: false,
    validationSchema: ShipmentSchema(requiredMeasures()),
    initialValues: savedValues ?? INITIAL_VALUES,
    initialTouched: !!savedValues
      ? generateTouchedObject(shippingSend3ShipmentPropTypes, savedValues)
      : undefined,
    onSubmit: onSubmitSuccess,
  })
  const shippingWeightDeclared = formik.values.packages.reduce(
    (shippingWeightDeclared, pack) => {
      const packageWeight = computeShippingWeight(pack.weight, {
        weight: pack.weight,
        height: pack.height,
        width: pack.width,
        length: pack.length,
      })

      return shippingWeightDeclared + packageWeight
    },
    0,
  )
  const packages = formik.values.packages.map(pack => ({
    weight: pack.weight,
    length: pack.length,
    width: pack.width,
    height: pack.height,
  }))

  const isToCanaryIslands = () => {
    return (
      destinCountryCode === 'ES' &&
      (destinPostalCode.substr(0, 2) === '38' ||
        destinPostalCode.substr(0, 2) === '35')
    )
  }

  const noAllowExcess = () => {
    return (
      (destinCountryCode === 'ES' &&
        (destinPostalCode.substr(0, 2) === '38' ||
          destinPostalCode.substr(0, 2) === '35' ||
          destinPostalCode.substr(0, 2) === '51' ||
          destinPostalCode.substr(0, 2) === '52')) ||
      destinCountryCode === 'AD' ||
      (originCountryCode === 'ES' &&
        (originPostalCode.substr(0, 2) === '38' ||
          originPostalCode.substr(0, 2) === '35' ||
          originPostalCode.substr(0, 2) === '51' ||
          originPostalCode.substr(0, 2) === '52')) ||
      destinCountryCode === 'AD'
    )
  }

  const requiredMeasures2 = () => {
    return (
      (destinCountryCode === 'ES' &&
        (destinPostalCode.substr(0, 2) === '38' ||
          destinPostalCode.substr(0, 2) === '35' ||
          destinPostalCode.substr(0, 2) === '51' ||
          destinPostalCode.substr(0, 2) === '52')) ||
      (originCountryCode === 'ES' &&
        (originPostalCode.substr(0, 2) === '38' ||
          originPostalCode.substr(0, 2) === '35' ||
          originPostalCode.substr(0, 2) === '51' ||
          originPostalCode.substr(0, 2) === '52')) ||
      formik.values?.serviceType?.originalCode === 'CIEL'
    )
  }

  const isPackageWeightValid = weight => {
    if (isPudo) {
      return weight <= SINGLE_PACKAGE_MAXIMUM_WEIGHT
    } else if (formik.values?.serviceType?.maxItemWeight < weight) {
      return false
    }

    return true
  }

  const areSinglePackageMeasureValid = packageDetail => {
    const packageDetailCopy = { ...packageDetail }
    delete packageDetailCopy.key
    delete packageDetailCopy.weight
    if (isPudo) {
      return Object.values(packageDetailCopy).every(
        measure => measure <= SINGLE_PACKAGE_MAXIMUM_MEASURE,
      )
    } else if (
      !Object.values(packageDetailCopy).every(
        measure => measure <= formik.values?.serviceType?.maxSideDimension,
      )
    ) {
      return false
    }
    return true
  }

  const findFailedMeasure = packageDetail => {
    const packageDetailCopy = { ...packageDetail }
    delete packageDetailCopy.key
    delete packageDetailCopy.weight

    const failedMeasureEntry = Object.entries(packageDetailCopy).find(
      ([measure, value]) => {
        const maxSideDimension = filteredServiceTypes?.find(
          ({ maxSideDimension }) => maxSideDimension < value,
        )?.maxSideDimension
        return value !== null && value > maxSideDimension
      },
    )

    const failedMeasure = failedMeasureEntry ? failedMeasureEntry[0] : null
    return failedMeasure || null
  }

  const arePackageMeasuresSumValid = packageDetail => {
    const { length, height, width } = packageDetail

    if (isPudo) {
      return length + height + width <= ALL_PACKAGE_MEASURES_MAXIMUM_SUM
    } else if (
      formik.values?.serviceType?.maxPerimeter <
      length + height + width
    ) {
      return false
    }

    return true
  }

  const emptyPackages = formik.values.packages.filter(checkPackageHasContent)

  const query = useServiceTypes({
    clientCenterCode: centreCode,
    originPostalCode,
    originCountryCode,
    destinPostalCode,
    destinCountryCode,
    packages,
  })

  const {
    data: serviceTypesData,
    isError: isErrorServiceTypes,
    isLoading: isLoadingServiceTypes,
    refetch: refetchServiceTypes,
  } = query

  const filteredServiceTypes = useMemo(() => {
    return isPudo
      ? serviceTypesData?.serviceTypes.filter(
          serviceType =>
            !['C10', 'C14', 'C14E'].includes(serviceType.originalCode),
        )
      : serviceTypesData?.serviceTypes
  }, [serviceTypesData, isPudo])

  const hasCustom = serviceTypesData?.hasCustom
  const allowsElectronicalTradeDocuments =
    serviceTypesData?.allowsElectronicalTradeDocuments

  const isServiceAllowed = serviceTypeOptions => {
    const filter = serviceTypeOptions.filter(
      ({ maxItemWeight, maxPerimeter, maxSideDimension }) =>
        formik?.values?.packages.find(
          ({ height, length, weight, width }) =>
            (maxPerimeter >= width + length + height || !maxPerimeter) &&
            (maxItemWeight >= weight || !maxItemWeight) &&
            (maxSideDimension >= width || !maxSideDimension) &&
            (maxSideDimension >= length || !maxSideDimension) &&
            (maxSideDimension >= height || !maxSideDimension),
        ),
    )
    return filter?.length > 0
  }

  const selectDefaultServiceType = useCallback(() => {
    if (filteredServiceTypes && filteredServiceTypes.length > 0) {
      if (
        formik.values.serviceType === null
        //&& isServiceAllowed(filteredServiceTypes)
      ) {
        //Prioriza poner por defecto 24h
        let found24h = filteredServiceTypes.find(
          ({ originalCode }) => originalCode === 'C24',
        )
        if (found24h) formik.setFieldValue('serviceType', found24h)
        else formik.setFieldValue('serviceType', filteredServiceTypes[0])
      }
    }
  }, [filteredServiceTypes, formik])

  useEffect(() => {
    selectDefaultServiceType()
  }, [selectDefaultServiceType])

  // Dado que ahora mismo la documentación necesaria para aduanas se está
  // alojando directamente en el navegador, dejo comentada esta petición
  // porque en el futuro se volverá a utilizar esta query (services/user).
  // const { data: transitRulesData } = useTransitRules({
  //   clientCenterCode: centreCode,
  //   originPostalCode,
  //   originCountryCode,
  //   destinPostalCode,
  //   destinCountryCode,
  //   serviceType: formik.values.serviceType,
  //   hasCustom,
  // })

  const [packageErrors, setPackageErrors] = useState([])

  const enhancedFormik = useEnhancedFormik(
    shippingSend3ShipmentPropTypes,
    formik,
    { fieldErrors: serverFieldErrors },
  )

  useEffect(() => {
    enhancedFormik.handleChange({
      target: {
        name: 'hasCustom',
        value: {
          custom: hasCustom && merchandise,
          allowsElectronicalTradeDocuments: allowsElectronicalTradeDocuments,
        },
      },
    })
    setCustom({
      custom: hasCustom && merchandise,
      allowsElectronicalTradeDocuments: allowsElectronicalTradeDocuments,
    })
  }, [hasCustom, merchandise, allowsElectronicalTradeDocuments])

  const { handleDiscardConfirmation, hasServerError, hasAnyError } =
    useShippingSendStep({
      formik,
      enhancedFormik,
      onSetDirty,
      hasConfirmation,
      onAcceptConfirmation,
      serverFieldErrors,
      propTypes: shippingSend3ShipmentPropTypes,
    })

  const handleAddPackage = async () => {
    await formik.setValues(
      values => ({
        ...values,
        packages: [
          ...values.packages,
          {
            key: generateRandomKey(),
            weight: null,
            length: null,
            width: null,
            height: null,
          },
        ],
      }),
      false,
    )
  }

  const handleRemovePackage = async index => {
    await formik.setFormikState(state => {
      const newPackages =
        state.values.packages.length > 1
          ? // Remove package
            [
              ...state.values.packages.slice(0, index),
              ...state.values.packages.slice(index + 1),
            ]
          : // Clear package values if there's only 1 package
            [
              {
                key: state.values.packages[0].key,
                weight: null,
                length: null,
                width: null,
                height: null,
              },
            ]

      return {
        ...state,
        values: {
          ...state.values,
          packages: newPackages,
        },
        touched: {
          ...state.touched,
          packages: [
            ...(state.touched.packages?.slice(0, index) ?? []),
            ...(state.touched.packages?.slice(index + 1) ?? []),
          ],
        },
        errors: {
          ...state.errors,
          packages: [
            ...(state.errors.packages?.slice(0, index) ?? []),
            ...(state.errors.packages?.slice(index + 1) ?? []),
          ],
        },
      }
    })
    setCompletedPackages(prev => prev.filter(pkg => pkg.index !== index))
    refetchServiceTypes()
  }

  const deletePackageErrorById = index =>
    setPackageErrors(packageErrors => {
      const newPackageErrors = [...packageErrors].filter(
        (error, idx) => idx !== index && error !== undefined,
      )

      return newPackageErrors
    })

  const setPackageErrorFromIndex = (index, errorKey, errorType) =>
    setPackageErrors(packageErrors => {
      const newPackageErrors = [...packageErrors]

      newPackageErrors[index] = {
        key: errorKey,
        errorType: errorType,
      }

      return newPackageErrors
    })

  const onPackageRowBlur = (e, index) => {
    formik.handleBlur(e)

    const packageDetail = formik.values.packages[index]

    const isWeightValid = isPackageWeightValid(packageDetail.weight)
    const areEveryMeasuresValid = areSinglePackageMeasureValid(packageDetail)
    const areAllMeasuresSumValid = arePackageMeasuresSumValid(packageDetail)
    const hasMaxLimitService =
      formik.values?.serviceType?.maxItemWeight &&
      formik.values?.serviceType?.maxPerimiter &&
      formik.values?.serviceType?.maxMeasure &&
      formik.values?.serviceType?.maxSideDimension

    if (
      (!requiredMeasures2() && packageDetail.weight !== null) ||
      (requiredMeasures2() &&
        packageDetail.weight !== null &&
        packageDetail.length !== null &&
        packageDetail.width !== null &&
        packageDetail.height !== null)
    ) {
      if (packageDetail.weight === 0) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:weight-minimum'),
          'weight',
        )
      } else if (hasMaxLimitService && !hasMaxLimitService) {
        deletePackageErrorById(index)
      } else if (requiredMeasures2 && packageDetail.length === 0) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:length-minimum'),
          'length',
        )
      } else if (requiredMeasures2 && packageDetail.width === 0) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:width-minimum'),
          'width',
        )
      } else if (requiredMeasures2 && packageDetail.height === 0) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:height-minimum'),
          'height',
        )
      } else if (!isWeightValid || packageDetail.weight > 300) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:weight-exceeded', {
            maxWeight: isPudo
              ? SINGLE_PACKAGE_MAXIMUM_WEIGHT
              : packageDetail.weight > 300
              ? 300
              : formik.values?.serviceType?.maxItemWeight,
          }),
          'weight',
        )
      } else if (!areEveryMeasuresValid && formik.values?.serviceType) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:measure-exceeded', {
            maxMeasure: isPudo
              ? SINGLE_PACKAGE_MAXIMUM_MEASURE
              : formik.values?.serviceType?.maxSideDimension,
          }),
          findFailedMeasure(packageDetail),
        )
      } else if (!areAllMeasuresSumValid) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:measures-sum-exceeded-pudos',
            {
              maxPerimeter: isPudo
                ? ALL_PACKAGE_MEASURES_MAXIMUM_SUM
                : formik.values?.serviceType?.maxPerimeter,
            },
          ),
          'perimeter',
        )
      } else if (noAllowExcess() && Number(shippingWeightDeclared.toFixed(2))>30) {
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:measures-sum-exceeded'),
          'perimeter',
        )
      } else {
        deletePackageErrorById(index)
      }
    } else if (
      (!requiredMeasures2() && packageDetail.weight === null) ||
      (requiredMeasures2() &&
        (packageDetail.weight === null ||
          packageDetail.length === null ||
          packageDetail.width === null ||
          packageDetail.height === null))
    ) {
      if (packageDetail.weigth !== null) {
        if (packageDetail.weight === 0) {
          setPackageErrorFromIndex(
            index,
            t('error:field:package-row:weight-minimum'),
            'weight',
          )
        } else if (!isWeightValid || packageDetail.weight > 300) {
          setPackageErrorFromIndex(
            index,
            t('error:field:package-row:weight-exceeded', {
              maxWeight: isPudo
                ? SINGLE_PACKAGE_MAXIMUM_WEIGHT
                : packageDetail.weight > 300
                ? 300
                : formik.values?.serviceType?.maxItemWeight,
            }),
            'weight',
          )
        } else
          setPackageErrorFromIndex(
            index,
            t('error:field:package-row:empty-fields'),
          )
      } else
        setPackageErrorFromIndex(
          index,
          t('error:field:package-row:empty-fields'),
        )
    } else {
      deletePackageErrorById(index)
    }
  }

  const onPackageRowFocus = (e, index) => {
    const fieldName = e.target.name.split('.')[1]

    formik.setFormikState(state => {
      const newTouchedPackages = state.touched.packages
        ? [...state.touched?.packages]
        : []

      newTouchedPackages[index] = {
        ...newTouchedPackages[index],
        [fieldName]: true,
      }

      return {
        ...state,
        touched: {
          ...state.touched,
          packages: newTouchedPackages,
        },
      }
    })
  }

  const packageWeights = formik.values.packages.map(
    packageInfo => packageInfo.weight,
  )

  useEffect(() => {
    if (hasConfirmation && !hasAnyError) onSubmitSuccess(formik.values)
  }, [formik.values, hasAnyError, hasConfirmation, onSubmitSuccess])

  const debouncedPackages = useDebounce(formik.values.packages, 1000)

  const debouncedCompleted = useDebounce(completedPackages, 1000)

  useEffect(() => {
    formik.values.packages.forEach((pack, index) => {
      const weightFilled = pack.weight !== null && pack.weight > 0
      const dimensionsFilled =
        pack.length !== null && pack.width !== null && pack.height !== null

      if (weightFilled) {
        const alreadyCompleted = completedPackages.find(
          pkg => pkg.index === index && pkg.type === 'weight',
        )
        if (
          !alreadyCompleted ||
          (alreadyCompleted && pack.weight !== alreadyCompleted.weight)
        ) {
          refetchServiceTypes()
          setCompletedPackages(prev => [
            ...prev.filter(pkg => pkg.index !== index || pkg.type !== 'weight'),
            { index, type: 'weight', weight: pack.weight },
          ])
        }
      }

      if (dimensionsFilled) {
        const alreadyCompleted = completedPackages.find(
          pkg => pkg.index === index && pkg.type === 'dimensions',
        )
        if (
          !alreadyCompleted ||
          (alreadyCompleted &&
            (pack.length !== alreadyCompleted.length ||
              pack.width !== alreadyCompleted.width ||
              pack.height !== alreadyCompleted.height))
        ) {
          refetchServiceTypes()
          setCompletedPackages(prev => [
            ...prev.filter(
              pkg => pkg.index !== index || pkg.type !== 'dimensions',
            ),
            {
              index,
              type: 'dimensions',
              length: pack.length,
              width: pack.width,
              height: pack.height,
            },
          ])
        }
      }
    })
  }, [debouncedPackages, debouncedCompleted, refetchServiceTypes])

  return (
    <ShippingSendForm onSubmit={enhancedFormik.handleSubmit}>
      {/* TODO: pasar el nombre de la documentación, su tipo y su path */}
      <Step3ShipmentUI
        values={formik.values}
        touched={formik.touched}
        originOrDestinAddressChanged={originOrDestinAddressChanged}
        originAddressChanged={originAddressChanged}
        fieldErrors={enhancedFormik.fieldErrors}
        formErrors={enhancedFormik.formErrors}
        onChange={enhancedFormik.handleChange}
        onBlur={formik.handleBlur}
        onPackageRowBlur={onPackageRowBlur}
        isAddButtonDisabled={
          packageErrors.length > 0 ||
          packageWeights.some(weight => weight === null) ||
          isPudo
        }
        requiredMeasures={requiredMeasures2()}
        serviceTypeOptions={filteredServiceTypes}
        isErrorServiceTypes={isErrorServiceTypes}
        isLoadingServiceTypeOptions={isLoadingServiceTypes}
        onAddPackage={handleAddPackage}
        onRemovePackage={handleRemovePackage}
        totalWeight={Number(shippingWeightDeclared.toFixed(2))}
        packageErrors={packageErrors}
        onPackageRowFocus={onPackageRowFocus}
        showInfo={formik.values.serviceType === null}
        hasCustoms={hasCustom}
        allowsElectronicalTradeDocuments={allowsElectronicalTradeDocuments}
        isToCanaryIslands={isToCanaryIslands()}
        international={international}
        imputationCenter={imputationCenter}
        setMerchandise={setMerchandise}
        isOriginPudo={isOriginPudo}
      />

      <ActionButtons
        isPrimaryDisabled={
          emptyPackages.length === 0 ||
          !filteredServiceTypes?.length ||
          packageErrors.length > 0 ||
          formik.values.serviceType === null
        }
        isDirty={formik.dirty}
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={hasServerError}
        currentStep={STEPS.SHIPMENT}
        activeStep={activeStep}
      />

      {hasConfirmation && hasAnyError ? (
        <ConfirmationWithErrorModal
          onDiscard={handleDiscardConfirmation}
          onAmend={onCancelConfirmation}
        />
      ) : null}
    </ShippingSendForm>
  )
}

Step3Shipment.propTypes = {
  savedValues: nullablePropType(
    makeValuesPropType(shippingSend3ShipmentPropTypes).isRequired,
  ),
  originOrDestinAddressChanged: PropTypes.bool,
  originAddressChanged: PropTypes.bool,
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend3ShipmentPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSetDirty: PropTypes.func.isRequired,
  hasConfirmation: PropTypes.bool.isRequired,
  onCancelConfirmation: PropTypes.func.isRequired,
  onAcceptConfirmation: PropTypes.func.isRequired,
  isPudo: PropTypes.bool.isRequired,
  imputationCenter: PropTypes.string,
  international: PropTypes.bool.isRequired,
}

export default Step3Shipment
